<template>
  <div class="transpress-page">
    <div class="nav-bg">
      <div class="nav-wrap">
        <NavigationBar />
      </div>
    </div>

    <!-- Hero Section (full-width split) -->
    <section class="hero-full">
      <div class="hero-full__grid">
        <div class="hero-full__copy">
          <div class="hero-eyebrow">
            <span class="hero-eyebrow__dot"></span>
            <span>TRANSPRESS × MOMFIDENT</span>
          </div>
          <h1 class="hero-title text-style-home-heading">
            {{ $t('transpress-hero-title') }}
          </h1>
          <p class="hero-subtitle">
            {{ $t('transpress-hero-subtitle') }}
          </p>

          <div class="hero-actions">
            <button class="btn-pink" type="button" @click="goToDownload">
              {{ $t('zdraveto-cta-btn') }}
            </button>
            <button class="btn-ghost" type="button" @click="scrollToInitiative">
              {{ $t('transpress-btn-ghost') }}
            </button>
          </div>
        </div>

        <div class="hero-full__media">
          <div class="hero-image">
            <img src="/static/transpress/hero.png" alt="TRANSPRESS × Momfident" />
          </div>
        </div>
      </div>
    </section>

    <!-- Initiative / Interview -->
    <section id="initiative" ref="initiativeRef" class="interview container">
      <p class="interview__lead">
         {{ $t('transpress-interview') }}
      </p>

      <div class="interview__grid">
        <aside class="interview__aside">
          <div class="interview__portrait-card">
            <img src="/static/transpress/natali-mihova.png" alt="Натали Михова" />
            <div class="interview__person">
              <span class="interview__person-name">{{ $t('transpress-person-name') }}</span>
              <span class="interview__person-position">{{ $t('transpress-person-position') }}</span>
              <span class="interview__person-role">{{ $t('transpress-person-role') }}</span>
            </div>
          </div>
        </aside>

        <div class="interview__qa">
          <div class="interview__qa-header">
            <div class="interview__eyebrow">{{ $t('transpress-interview-eyebrow') }}</div>
            <h2 class="interview__title text-style-home-heading">{{ $t('transpress-interview-text') }}а</h2>
          </div>

              <div v-for="(qa, i) in interview" :key="i" class="qa-item">
              <div class="qa-item__q">
                <span class="qa-item__num">
                  {{ String(i + 1).padStart(2, '0') }}
                </span>

                <h4 class="qa-item__question">
                  {{ $t(qa.q) }}
                </h4>
              </div>

              <div class="qa-item__a">
                <p
                  v-for="(p, j) in qa.a"
                  :key="j"
                  class="qa-item__answer"
                >
                  {{ $t(p) }}
                </p>
              </div>
            </div>
        </div>
      </div>
    </section>

    <!-- Gallery with lightbox -->
    <section class="gallery container">
      <div class="gallery__header">
        <div class="gallery__eyebrow">{{ $t('transpress-gallery') }}</div>
      </div>

      <div class="gallery__grid">
        <button
          v-for="(img, i) in gallery"
          :key="i"
          class="gallery__thumb"
          type="button"
          :aria-label="`Отвори снимка ${i + 1}`"
          @click="openLightbox(i)"
        >
          <img :src="img.src" :alt="img.alt" loading="lazy" />
        </button>
      </div>
    </section>

    <!-- Lightbox overlay -->
    <Transition name="lightbox">
      <div
        v-if="lightboxIndex !== null"
        class="lightbox"
        role="dialog"
        aria-modal="true"
        @click.self="closeLightbox"
      >
        <button class="lightbox__close" type="button" aria-label="Затвори" @click="closeLightbox">
          ×
        </button>
        <button
          class="lightbox__nav lightbox__nav--prev"
          type="button"
          aria-label="Предишна"
          @click.stop="prevImg"
        >
          ‹
        </button>
        <div class="lightbox__stage">
          <img
            :key="lightboxIndex"
            :src="gallery[lightboxIndex].src"
            :alt="gallery[lightboxIndex].alt"
            class="lightbox__img"
          />
        </div>
        <button
          class="lightbox__nav lightbox__nav--next"
          type="button"
          aria-label="Следваща"
          @click.stop="nextImg"
        >
          ›
        </button>
        <div class="lightbox__counter">{{ lightboxIndex + 1 }} / {{ gallery.length }}</div>
      </div>
    </Transition>

    <!-- Partner case study (subtle band) -->
    <section class="casestudy">
      <div class="container casestudy__container">
        <div class="casestudy__eyebrow">{{ $t('transpress-casestudy-eyebrow') }}</div>
        <h2 class="casestudy__title">{{ $t('transpress-casestudy-title') }}</h2>

        <p class="casestudy__p">
          {{ $t('transpress-casestudy-text-1') }}
        </p>
        <p class="casestudy__p casestudy__p--highlight">
          {{ $t('transpress-casestudy-text-2') }}
        </p>

        <button class="btn-pink casestudy__cta" type="button" @click="goToPartnership">
          {{ $t('transpress-casestudy-btn') }}
        </button>
      </div>
    </section>

    <!-- App Support (inverse pink band) -->
    <section class="app-support">
      <div class="app-support__leaf" aria-hidden="true"></div>
      <div class="container">
        <h2 class="app-support__title">{{ $t('zdraveto-app-support-title') }}</h2>
        <div class="app-support__grid">
          <div class="app-support__phone">
            <img src="/static/momfident_phone_screen.png" alt="Momfident App" />
          </div>
          <div class="app-support__features">
            <div v-for="(f, i) in features" :key="i" class="feature-row">
              <div class="feature-row__num">0{{ i + 1 }}</div>
              <div class="feature-row__body">
                <h4 class="feature-row__title">{{ $t(f.titleKey) }}</h4>
                <p class="feature-row__text">{{ $t(f.textKey) }}</p>
              </div>
            </div>

            <div class="app-support__actions">
              <button class="btn-white" type="button" @click="goToDownload">
                {{ $t('zdraveto-cta-btn') }}
              </button>
              <div class="store-badges">
                <a href="https://apps.apple.com/us/app/momfident/id6751100834" target="_blank" rel="noopener noreferrer">
                  <AppStore />
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.momfident.app" target="_blank" rel="noopener noreferrer">
                  <GooglePlay />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Testimonials -->
    <section class="reviews container">
      <div class="reviews__header">
        <div class="reviews__eyebrow">{{ $t('zdraveto-reviews-eyebrow') }}</div>
        <h2 class="reviews__title text-style-home-heading" v-html="$t('zdraveto-reviews-title')"></h2>
      </div>
      <HomeTestimonialsComponent />
    </section>

    <!-- Footer -->
    <div class="footer-wrap">
      <FooterComponent :show-logos="false" />
    </div>
  </div>
</template>

    <script setup>

    import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
    import { useRouter } from 'vue-router';
    import NavigationBar from '@/components/NavigationBar.vue';
    import FooterComponent from '@/components/Components/FooterComponent.vue';
    import HomeTestimonialsComponent from '@/components/Components/Home/HomeTestimonialsComponent.vue';
    import AppStore from '@/components/Common/Buttons/AppStore.vue';
    import GooglePlay from '@/components/Common/Buttons/GooglePlay.vue';

    const router = useRouter();

    const initiativeRef = ref(null);
    const lightboxIndex = ref(null);

    const gallery = [
      { src: '/static/transpress/hero.png', alt: 'Мениджър пред камион DAF на TRANSPRESS' },
      { src: '/static/transpress/two-women-front.jpg', alt: 'Две служителки пред камион MAN на TRANSPRESS' },
      { src: '/static/transpress/driver-wheel.jpg', alt: 'Шофьорка зад волана на камион TRANSPRESS' },
      { src: '/static/transpress/two-women-between.jpg', alt: 'Две служителки между камиони TRANSPRESS' },
      { src: '/static/transpress/two-women-back.jpg', alt: 'Две служителки пред камион MAN на TRANSPRESS' },
      { src: '/static/transpress/two-women-smile.jpg', alt: 'Две усмихнати служителки между камиони TRANSPRESS' },
      { src: '/static/transpress/team.jpg', alt: 'Екипът на TRANSPRESS на събитието' },
      { src: '/static/transpress/flowers.jpg', alt: 'Служителка с цветя пред склада на TRANSPRESS' },
      { src: '/static/transpress/mirror.jpg', alt: 'Отражение на служителка в огледалото на камион' },
      { src: '/static/transpress/cab-suit.png', alt: 'Мениджър в кабината на камион TRANSPRESS' },
      { src: '/static/transpress/thirty-years.png', alt: '30 години TRANSPRESS' },
    ];

    const interview = [
      {
        q: 'transpress-qa-1-question',
        a: [
          'transpress-qa-1-answer-1',
          'transpress-qa-1-answer-2',
        ],
      },
      {
        q: 'transpress-qa-2-question',
        a: [
          'transpress-qa-2-answer-1',
          'transpress-qa-2-answer-2',
        ],
      },

      {
        q: 'transpress-qa-3-question',
        a: [
          'transpress-qa-3-answer-1',
          'transpress-qa-3-answer-2',
        ]
      },
      {
        q: 'transpress-qa-4-question',
        a: [
          'transpress-qa-4-answer-1',
          'transpress-qa-4-answer-2',
        ],
      },
      {
        q: 'transpress-qa-5-question',
        a: [
          'transpress-qa-5-answer-1',
          'transpress-qa-5-answer-2',
        ],
      },

      {
        q: 'transpress-qa-6-question',
        a: [
          'transpress-qa-6-answer-1',
          'transpress-qa-6-answer-2',
        ]
      },
      {
        q: 'transpress-qa-7-question',
        a: [
          'transpress-qa-7-answer-1',
          'transpress-qa-7-answer-2',
        ],
      },
      {
        q: 'transpress-qa-8-question',
        a: [
          'transpress-qa-8-answer-1',
          'transpress-qa-8-answer-2',
        ],
      },

      {
        q: 'transpress-qa-9-question',
        a: [
          'transpress-qa-9-answer-1',
          'transpress-qa-9-answer-2',
        ]
      },

      {
        q: 'transpress-qa-10-question',
        a: [
          'transpress-qa-10-answer-1',
          'transpress-qa-10-answer-2',
        ]
      }
      // и т.н.
    ];

// const interview = [
//   {
//     q: 'Как бихте описали културата на TRANSPRESS по отношение на грижата към служителите? А каква специфична грижа имате към жените във Вашата компания?',
//     a: [
//       'В TRANSPRESS винаги сме вярвали, че успешният бизнес се гради от хора, които се чувстват ценени и подкрепени. Затова грижата за служителите не е отделна инициатива, а част от ежедневната ни работа и начин на мислене.',
//       'По отношение на жените в компанията се стремим да създаваме среда, в която те могат да се развиват професионално, без да се налага да избират между кариера и семейство. В екипа ни има много жени на ключови и ръководни позиции, а за нас това никога не е било въпрос на квота или политика, а на качества, потенциал и доверие.',
//     ],
//   },
//   {
//     q: 'TRANSPRESS е компания в индустрия, която често се възприема като по-скоро мъжка. Какво означава за Вас да имате жени на ръководни позиции и как това влияе на корпоративната култура?',
//     a: [
//       'Логистиката традиционно се свързва повече с мъжете, но реалността днес е различна. В TRANSPRESS имаме жени, които управляват екипи, процеси, клиенти и цели бизнес направления.',
//       'За мен присъствието на жени в управлението носи различна перспектива, повече емпатия, по-добър баланс и често по-силна чувствителност към нуждите на хората. Това се отразява директно върху корпоративната култура и създава среда, в която различните гледни точки се ценят и уважават.',
//     ],
//   },
//   {
//     q: 'Какви са най-честите предизвикателства, които виждате при жените и майките в работна среда?',
//     a: [
//       'Най-често виждам вътрешното напрежение, което много жени изпитват. Желанието да бъдат добри професионалисти и едновременно с това добри майки често ги кара да поставят прекалено високи изисквания към себе си.',
//       'Като майка на шестгодишно момче добре познавам това чувство. Понякога най-голямото предизвикателство не е организацията на времето, а усещането, че трябва да бъдеш навсякъде и да даваш най-доброто от себе си постоянно.',
//     ],
//   },
//   {
//     q: 'Как подкрепяте служителките, които са майки както по време на майчинство, така и при завръщане на работа?',
//     a: [
//       'Стремим се да поддържаме връзка със служителките и по време на майчинство, ако те самите желаят това. Каним ги на фирмени събития, споделяме новини от компанията и се стараем да останат част от общността.',
//       'При завръщането им най-важното е да има разбиране и плавна адаптация. Винаги подхождаме индивидуално, защото всяка майка има различни нужди и различна семейна ситуация.',
//     ],
//   },
//   {
//     q: 'Има ли теми около майчинството и работата, за които според Вас все още се говори трудно в корпоративна среда?',
//     a: [
//       'Да. Все още корпоративното общество и като цяло бизнеса все още не говорят достатъчно открито за умората, прегарянето, чувството за вина и психическото натоварване, което съпътства много майки.',
//       'Често жените се опитват да изглеждат сякаш се справят с всичко без усилие, а истината е, че зад това стоят много предизвикателства, които рядко се споделят открито.',
//     ],
//   },
//   {
//     q: 'Какво Ви накара да припознаете Momfident като смислена подкрепа за Вашите служители - майки?',
//     a: [
//       'Това, което най-много ме впечатли, е че Momfident разглежда майката като цялостен човек, а не само през ролята ѝ на родител или служител.',
//       'Когато една жена получава подкрепа за своето емоционално благополучие, увереност и личностно развитие, това се отразява положително както на нея самата, така и на семейството и професионалния ѝ живот.',
//     ],
//   },
//   {
//     q: 'Какви други инициативи развивате в подкрепа на жените в екипа Ви?',
//     a: [
//       'Винаги влизаме в положението на родителите и при извънредна ситуация няма проблем детето да дойде в офиса, имаме и отделен кът затова, за да може да се забавляват докато родителят работи. Също така инвестираме активно в обучения, лидерски програми и възможности за професионално развитие. Насърчаваме вътрешното израстване и създаваме условия хората да развиват потенциала си независимо от позицията, която заемат.',
//       'Освен това организираме различни събития и инициативи, които подкрепят баланса между професионалния и личния живот и насърчават свързаността между хората в екипа.',
//     ],
//   },
//   {
//     q: 'Защо според Вас психичното здраве и емоционалната подкрепа на майките трябва да бъдат част от грижата на работодателя?',
//     a: [
//       'Защото зад всеки служител стои човек със своите предизвикателства, тревоги и отговорности. Няма как да говорим за устойчиво развитие на хората и бизнеса, ако пренебрегваме тази страна от живота им.',
//       'Психичното здраве не е личен проблем, който остава пред вратата на офиса. То влияе на мотивацията, ангажираността, взаимоотношенията и цялостното благополучие на човека.',
//     ],
//   },
//   {
//     q: 'Какво послание искате да дадете към жените и майките в екипа си чрез това партньорство?',
//     a: [
//       'Че не са сами.',
//       'Че е напълно нормално понякога да се чувстват несигурни, изморени или претоварени. И че няма нужда да се справят с всичко сами.',
//       'Искам всяка жена в нашия екип да знае, че я виждаме не само като професионалист, а и като човек със своите нужди, мечти и предизвикателства.',
//     ],
//   },
//   {
//     q: 'Какво бихте казали на други компании, които се колебаят дали темата за майчинството и психичното здраве има място в работната среда?',
//     a: [
//       'Бих ги насърчила да погледнат отвъд краткосрочните резултати и да инвестират в хората си.',
//       'Когато една компания показва истинска грижа към служителите си, тя изгражда доверие, лоялност и по-силна организационна култура. А това са неща, които не могат да бъдат измерени само в цифри, но се усещат всеки ден.',
//       'В крайна сметка успешните компании се създават от хора, а хората дават най-доброто от себе си тогава, когато знаят, че са разбрани и подкрепени.',
//     ],
//   },
// ];

const features = [
  { titleKey: 'zdraveto-feature-1-title', textKey: 'zdraveto-feature-1-text' },
  { titleKey: 'zdraveto-feature-2-title', textKey: 'zdraveto-feature-2-text' },
  { titleKey: 'zdraveto-feature-3-title', textKey: 'zdraveto-feature-3-text' },
];

function goToDownload() {
  router.push({ name: 'download' });
}

function goToPartnership() {
  router.push({ name: 'partnership' });
}

function scrollToInitiative() {
  initiativeRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function openLightbox(i) {
  lightboxIndex.value = i;
}

function closeLightbox() {
  lightboxIndex.value = null;
}

function nextImg() {
  if (lightboxIndex.value === null) return;
  lightboxIndex.value = (lightboxIndex.value + 1) % gallery.length;
}

function prevImg() {
  if (lightboxIndex.value === null) return;
  lightboxIndex.value = (lightboxIndex.value - 1 + gallery.length) % gallery.length;
}

function onKeydown(e) {
  if (lightboxIndex.value === null) return;
  if (e.key === 'Escape') closeLightbox();
  else if (e.key === 'ArrowRight') nextImg();
  else if (e.key === 'ArrowLeft') prevImg();
}

// Lock body scroll while the lightbox is open.
watch(lightboxIndex, (val) => {
  document.body.style.overflow = val !== null ? 'hidden' : '';
});

onMounted(() => window.addEventListener('keydown', onKeydown));
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown);
  document.body.style.overflow = '';
});
</script>

<style scoped lang="scss">
@import "~@/assets/scss/fonts.scss";
@import "~@/assets/scss/variables.scss";

/* ==================== BASE ==================== */
.text-style-home-heading {
  font-family: $font-thin;
  font-weight: 300;
  line-height: 1.08;
  color: $color-purple;
  letter-spacing: -0.5px;
}

.transpress-page {
  background: #FFEFF2;
  color: $color-purple;
  font-family: 'Rubik', sans-serif;
  overflow-x: hidden;
}

.nav-bg {
  background: #ffffff;
  padding: 16px 0;
}

.nav-bg :deep(.my-4) {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

.nav-wrap {
  max-width: 1200px;
  margin: 0 auto;
  padding-left: 8px;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 40px;
}

/* ==================== HERO (FULL-WIDTH SPLIT, FLAT) ==================== */
.hero-full {
  width: 100%;
  background: #fff;
  margin-top: 0;
}

.hero-full__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 64px;
  align-items: center;
  min-height: 640px;
  padding: 80px 64px;
}

.hero-full__copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 620px;
}

.hero-full__media {
  width: 100%;
}

.hero-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #FFEFF2;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 11px;
  color: $color-pink-dark-2;
  font-weight: 600;
  letter-spacing: 1px;
  margin-bottom: 22px;

  &__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: $color-pink-dark-2;
  }
}

.hero-title {
  font-size: 44px;
  line-height: 1.12;
  margin: 0;
  letter-spacing: -0.5px;
  text-align: left;
}

.hero-subtitle {
  font-size: 18px;
  color: $color-purple;
  opacity: 0.85;
  margin-top: 18px;
  margin-bottom: 28px;
  line-height: 1.5;
  max-width: 520px;
  text-align: left;
}

.hero-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-start;
}

.hero-image {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 11;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 24px 48px -18px rgba(107, 107, 156, 0.4);

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

/* ==================== BUTTONS ==================== */
.btn-pink {
  background: linear-gradient(135deg, #FC678B 0%, #EA708C 100%);
  color: #fff;
  border: none;
  padding: 14px 28px;
  border-radius: 25px;
  font-family: 'Rubik-SemiBold', sans-serif;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.5px;
  cursor: pointer;
  box-shadow: 0 4px 10px rgba(252, 103, 139, 0.35);
  transition: transform 0.25s ease, box-shadow 0.25s ease, filter 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 18px rgba(252, 103, 139, 0.45);
    filter: brightness(1.05);
  }
}

.btn-ghost {
  background: transparent;
  color: $color-purple;
  border: 2px solid #E3DEEF;
  padding: 12px 22px;
  border-radius: 25px;
  font-family: 'Rubik', sans-serif;
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: transform 0.25s ease, border-color 0.25s ease, color 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: $color-pink-dark-2;
    color: $color-pink-dark-2;
  }
}

.btn-white {
  background: #fff;
  color: $color-pink-dark-2;
  border: none;
  padding: 14px 28px;
  border-radius: 25px;
  font-family: 'Rubik-SemiBold', sans-serif;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.5px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 22px rgba(0, 0, 0, 0.18);
  }
}

/* ==================== INTERVIEW ==================== */
.interview {
  margin-top: 90px;
  scroll-margin-top: 20px;
}

.interview__lead {
  max-width: 900px;
  margin: 0 auto 56px;
  text-align: center;
  font-size: 19px;
  line-height: 1.7;
  color: $color-purple;
  font-weight: 300;
}

.interview__grid {
  display: grid;
  grid-template-columns: 340px 1fr;
  gap: 56px;
  align-items: start;
}

.interview__aside {
  position: sticky;
  top: 30px;
}

.interview__portrait-card {
  background: #ffffff;
  border-radius: 24px;
  padding: 20px 20px 22px;
  box-shadow: 0 20px 44px -18px rgba(107, 107, 156, 0.4);
  text-align: center;

  img {
    width: 100%;
    height: auto;
    display: block;
    border-radius: 16px;
    object-fit: cover;
  }
}

.interview__person {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.interview__person-name {
  font-family: $font-semi-bold;
  font-weight: 600;
  font-size: 20px;
  color: $color-purple;
}

.interview__person-position {
  font-size: 15px;
  font-weight: 500;
  color: $color-purple;
}

.interview__person-role {
  font-size: 13px;
  letter-spacing: 1.5px;
  font-weight: 600;
  color: $color-pink-dark-2;
  text-transform: uppercase;
}

.interview__qa-header {
  margin-bottom: 10px;
}

.interview__eyebrow {
  font-size: 11px;
  color: $color-pink-dark-2;
  letter-spacing: 1.5px;
  font-weight: 600;
  margin-bottom: 12px;
}

.interview__title {
  font-size: 40px;
  line-height: 1.05;
  margin: 0 0 8px;
}

/* Q&A items — same visual language as the former accordion */
.qa-item {
  border-bottom: 1px solid #F0D8DE;
  padding: 26px 0;

  &:last-child { border-bottom: none; }
}

.qa-item__q {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.qa-item__num {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: $color-pink-dark-2;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: $font-semi-bold;
  font-size: 14px;
  font-weight: 600;
}

.qa-item__question {
  margin: 4px 0 0;
  font-size: 18px;
  font-weight: 500;
  color: $color-purple;
  line-height: 1.35;
  flex: 1;
}

.qa-item__a {
  margin-top: 14px;
  padding-left: 50px;
}

.qa-item__answer {
  font-size: 14px;
  line-height: 1.7;
  color: #4A4A4A;
  margin: 0 0 12px;

  &:last-child { margin-bottom: 0; }
}

/* ==================== GALLERY ==================== */
.gallery {
  margin-top: 90px;
}

.gallery__header {
  text-align: center;
  margin-bottom: 36px;
}

.gallery__eyebrow {
  font-size: 11px;
  color: $color-pink-dark-2;
  letter-spacing: 1.5px;
  font-weight: 600;
  margin-bottom: 12px;
}

.gallery__title {
  font-size: 40px;
  margin: 0;
}

.gallery__grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.gallery__thumb {
  position: relative;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  border-radius: 16px;
  overflow: hidden;
  aspect-ratio: 1 / 1;
  background: #f3e6ea;
  box-shadow: 0 12px 26px -14px rgba(107, 107, 156, 0.35);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.5s cubic-bezier(0.2, 0.8, 0.2, 1);
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(107, 107, 156, 0);
    transition: background 0.3s ease;
  }

  &:hover img { transform: scale(1.08); }
  &:hover::after { background: rgba(107, 107, 156, 0.12); }
}

/* ==================== LIGHTBOX ==================== */
.lightbox {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(20, 16, 30, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 72px;
}

.lightbox__stage {
  max-width: 100%;
  max-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.lightbox__img {
  max-width: 90vw;
  max-height: 88vh;
  border-radius: 10px;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.5);
  display: block;
  /* "lightspeed" snap-zoom each time the image changes */
  animation: lightbox-zoom 0.42s cubic-bezier(0.18, 0.9, 0.18, 1) both;
}

@keyframes lightbox-zoom {
  0% { opacity: 0; transform: scale(0.55); }
  100% { opacity: 1; transform: scale(1); }
}

.lightbox__close {
  position: absolute;
  top: 20px;
  right: 26px;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-size: 30px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover { background: rgba(255, 255, 255, 0.28); transform: scale(1.06); }
}

.lightbox__nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 54px;
  height: 54px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-size: 34px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover { background: rgba(255, 255, 255, 0.28); }
  &--prev { left: 20px; }
  &--next { right: 20px; }
}

.lightbox__counter {
  position: absolute;
  bottom: 22px;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
  letter-spacing: 1px;
}

/* overlay fade */
.lightbox-enter-active,
.lightbox-leave-active { transition: opacity 0.28s ease; }
.lightbox-enter-from,
.lightbox-leave-to { opacity: 0; }

/* ==================== PARTNER CASE STUDY (SUBTLE BAND) ==================== */
.casestudy {
  margin-top: 96px;
  background: #F4F2F9;
  padding: 72px 0;
}

.casestudy__container {
  max-width: 900px;
}

.casestudy__eyebrow {
  font-size: 11px;
  color: $color-pink-dark-2;
  letter-spacing: 1.5px;
  font-weight: 600;
  margin-bottom: 14px;
}

.casestudy__title {
  font-family: 'Rubik', sans-serif;
  font-weight: 500;
  font-size: 32px;
  color: $color-purple;
  margin: 0 0 24px;
  line-height: 1.2;
}

.casestudy__p {
  font-size: 16px;
  line-height: 1.75;
  color: #4A4A4A;
  margin: 0 0 18px;
}

.casestudy__p--highlight {
  background: #EBE7F3;
  padding: 20px 24px;
  border-radius: 12px;
  margin-bottom: 22px;
}

.casestudy__cta {
  margin-top: 14px;
}

/* ==================== APP SUPPORT (INVERSE PINK) ==================== */
.app-support {
  margin-top: 0;
  background: $color-pink-dark-2;
  padding: 70px 0;
  color: #fff;
  position: relative;
  overflow: hidden;
}

.app-support__leaf {
  position: absolute;
  bottom: -60px;
  right: -80px;
  width: 320px;
  height: 320px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.18), transparent 70%);
  filter: blur(6px);
  pointer-events: none;
}

.app-support__title {
  font-family: 'Rubik', sans-serif;
  font-weight: 300;
  font-size: 40px;
  text-align: center;
  margin: 0 0 44px;
  line-height: 1.1;
  letter-spacing: -0.5px;
}

.app-support__grid {
  display: grid;
  grid-template-columns: 5fr 7fr;
  gap: 56px;
  align-items: center;
  position: relative;
  z-index: 1;
}

.app-support__phone {
  display: flex;
  justify-content: center;

  img {
    max-width: 100%;
    max-height: 500px;
    filter: drop-shadow(0 24px 48px rgba(0, 0, 0, 0.25));
  }
}

.feature-row {
  display: flex;
  gap: 18px;
  align-items: flex-start;
  padding: 20px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);

  &:last-of-type { border-bottom: none; }

  &__num {
    font-family: 'Rubik', sans-serif;
    font-weight: 300;
    font-size: 40px;
    opacity: 0.5;
    line-height: 1;
    min-width: 50px;
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-family: $font-semi-bold;
    font-weight: 500;
    font-size: 17px;
    margin: 6px 0 8px;
    line-height: 1.3;
  }

  &__text {
    font-size: 13px;
    margin: 0;
    line-height: 1.6;
    opacity: 0.9;
  }
}

.app-support__actions {
  margin-top: 28px;
  display: flex;
  align-items: center;
  gap: 22px;
  flex-wrap: wrap;
}

.store-badges {
  display: flex;
  gap: 12px;
  align-items: center;

  a {
    display: inline-flex;
    transition: transform 0.2s ease;

    &:hover { transform: translateY(-3px); }
  }

  :deep(svg) {
    height: 44px;
    width: auto;
  }
}

/* ==================== REVIEWS ==================== */
.reviews {
  margin-top: 80px;
}

.reviews__header {
  text-align: center;
  margin-bottom: 40px;
}

.reviews__eyebrow {
  font-size: 11px;
  color: $color-pink-dark-2;
  letter-spacing: 1.5px;
  font-weight: 600;
  margin-bottom: 10px;
}

.reviews__title {
  font-size: 36px;
  margin: 0;
}

.reviews :deep(.testimonials-wrapper) {
  padding: 0;
  background: transparent;
}

.reviews :deep(.testimonials-wrapper > .container) {
  padding: 0;
}

/* ==================== FOOTER ==================== */
.footer-wrap {
  margin-top: 80px;
}

.footer-wrap :deep(.p-5) { display: none !important; }
.nav-wrap :deep(.logo) { margin-left: 10px; }
.nav-wrap :deep(.download-btn) { margin-right: 10px; }

/* ==================== RESPONSIVE ==================== */
@media (max-width: 1200px) {
  .hero-full__grid {
    gap: 40px;
    min-height: 560px;
    padding-block: 64px;
  }
  .hero-title { font-size: 38px; }
}

@media (max-width: 1100px) {
  .interview__grid { grid-template-columns: 300px 1fr; gap: 40px; }
  .interview__title,
  .gallery__title { font-size: 34px; }
  .app-support__title { font-size: 32px; }
}

@media (max-width: 992px) {
  .container { padding: 0 28px; }

  .hero-full__grid {
    grid-template-columns: 1fr;
    gap: 36px;
    min-height: 0;
    padding-block: 48px;
    padding-inline: 28px;
  }

  .hero-full__copy {
    max-width: 720px;
    margin: 0 auto;
    align-items: center;
    text-align: center;
  }

  .hero-title { font-size: 32px; text-align: center; }
  .hero-subtitle { font-size: 16px; text-align: center; margin-left: auto; margin-right: auto; }
  .hero-actions { justify-content: center; }

  .interview { margin-top: 64px; }

  .interview__grid {
    grid-template-columns: 1fr;
    gap: 28px;
  }

  .interview__qa { order: 1; }

  .interview__aside {
    order: 2;
    position: static;
    max-width: 360px;
    margin: 0 auto;
  }

  .gallery { margin-top: 64px; }
  .gallery__grid { grid-template-columns: repeat(3, 1fr); }

  .casestudy { margin-top: 72px; padding: 56px 0; }

  .app-support { padding: 56px 0; }
  .app-support__title { font-size: 28px; margin-bottom: 36px; }

  .app-support__grid {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .app-support__phone img { max-height: 360px; }

  .reviews { margin-top: 60px; }
  .reviews__title { font-size: 28px; }
}

@media (max-width: 768px) {
  .nav-wrap { max-width: 100%; padding-left: 12px; padding-right: 12px; }
  .container { padding: 0 16px; }

  .hero-full__grid {
    padding: 36px 16px;
    gap: 28px;
  }

  .hero-eyebrow {
    font-size: 10px;
    padding: 5px 12px;
    letter-spacing: 0.8px;
  }

  .hero-title { font-size: 26px; }
  .hero-subtitle { font-size: 14px; margin-top: 12px; margin-bottom: 20px; }

  .btn-pink,
  .btn-white {
    padding: 12px 22px;
    font-size: 12px;
  }

  .btn-ghost {
    padding: 10px 18px;
    font-size: 12px;
  }

  .interview__lead { font-size: 16px; margin-bottom: 40px; }
  .interview__title { font-size: 26px; }

  .qa-item { padding: 22px 0; }
  .qa-item__q { gap: 12px; }
  .qa-item__num { width: 30px; height: 30px; font-size: 13px; }
  .qa-item__question { font-size: 16px; }
  .qa-item__a { padding-left: 42px; }
  .qa-item__answer { font-size: 13px; }

  .gallery__title { font-size: 26px; }
  .gallery__grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }

  .lightbox { padding: 16px; }
  .lightbox__img { max-width: 94vw; max-height: 82vh; }
  .lightbox__nav { width: 44px; height: 44px; font-size: 28px; }
  .lightbox__nav--prev { left: 8px; }
  .lightbox__nav--next { right: 8px; }
  .lightbox__close { top: 12px; right: 14px; }

  .casestudy__title { font-size: 24px; }
  .casestudy__p { font-size: 15px; }

  .app-support { padding: 44px 0; }
  .app-support__title { font-size: 24px; margin-bottom: 28px; }

  .feature-row {
    gap: 14px;
    padding: 16px 0;

    &__num { font-size: 32px; min-width: 42px; }
    &__title { font-size: 15px; }
    &__text { font-size: 12px; }
  }

  .app-support__actions {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .store-badges {
    justify-content: center;

    :deep(svg) { height: 38px; }
  }

  .btn-white { width: 100%; }

  .reviews { margin-top: 48px; }
  .reviews__title { font-size: 24px; }
  .footer-wrap { margin-top: 56px; }
}

@media (max-width: 480px) {
  .hero-title { font-size: 23px; }
}
</style>
